import {Group, Loader, Paper, SimpleGrid, Stack, Text, Title} from '@mantine/core'

import {useCurrentUser} from 'src/auth/use-current-user'
import {useDispatchYards} from 'src/dispatch/use-dispatch'
import {canDispatchWork, canEditSales, canPublish, editBlockedNote} from 'src/jobs/edit-rights'
import {EditRightsProvider} from 'src/jobs/edit-rights-context'
import {ProjectDeployedEquipmentSection} from 'src/project-deployed-equipment/project-deployed-equipment-section'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {ProjectBillingTermsSection} from './project-billing-terms-section'
import {ProjectCustomerPocSection} from './project-customer-poc-section'
import {ProjectDetailsHeader} from './project-details-header'
import {ProjectDetailsSection} from './project-details-section'
import {ProjectInternalContactSection} from './project-internal-contact-section'
import {ProjectInvoicingSection} from './project-invoicing-section'
import {ProjectJobsSection} from './project-jobs-section'
import {ProjectSchedulesBand} from './project-schedules-band'
import {useProject, useProjectBillingTerms} from './use-projects'

type ProjectDetailsPageProps = {
    projectId: string
}

export function ProjectDetailsPage({projectId}: ProjectDetailsPageProps) {
    const {data: project, isLoading, isError, error} = useProject(projectId)
    const {data: billingTerms = []} = useProjectBillingTerms(projectId)
    const currentUser = useCurrentUser()
    const {data: yards = []} = useDispatchYards()

    if (isLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (isError || !project) {
        const message = error instanceof Error ? error.message : 'Failed to load order'
        const isNotFound = message.toLowerCase().includes('not found')

        return (
            <Stack gap="md">
                <Title order={1}>{isNotFound ? 'Order not found' : 'Order details'}</Title>
                <Text c={isNotFound ? 'dimmed' : 'red'}>
                    {isNotFound
                        ? 'This order may have been removed or the link is invalid.'
                        : message}
                </Text>
                <DetailPageBackLink to="/projects" listLabel="orders" />
            </Stack>
        )
    }

    const owners = {repId: project.internalContact?.id, projectCreatedById: project.createdById}
    const workYardId = project.dispatchYardId ?? null
    const canEdit = canEditSales(currentUser, owners, workYardId)
    const canDispatch = canDispatchWork(currentUser, owners, workYardId)
    const readOnlyNote = editBlockedNote({
        user: currentUser,
        owners,
        repName: project.internalContact?.name,
        workYardId,
        workYardName: yards.find((yard) => yard.id === workYardId)?.name,
        what: 'order',
    })

    return (
        <EditRightsProvider rights={{canEdit, canDispatch}}>
            <Stack gap="md">
                <ProjectDetailsHeader
                    project={project}
                    canEdit={canEdit}
                    readOnlyNote={readOnlyNote}
                />

                <fieldset
                    disabled={!canEdit}
                    style={{border: 0, padding: 0, margin: 0, minWidth: 0}}
                >
                    <Stack gap="md">
                        <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
                            <ProjectDetailsSection project={project} />

                            <Paper withBorder p="md" radius="md">
                                <Stack gap="sm">
                                    <Title order={4}>Contacts</Title>
                                    <ProjectInternalContactSection project={project} />
                                    <ProjectCustomerPocSection project={project} />
                                </Stack>
                            </Paper>
                        </SimpleGrid>

                        <ProjectBillingTermsSection projectId={projectId} jobs={project.jobs} />

                        <ProjectInvoicingSection project={project} />
                    </Stack>
                </fieldset>

                <fieldset
                    disabled={!canDispatch}
                    style={{border: 0, padding: 0, margin: 0, minWidth: 0}}
                >
                    <ProjectDeployedEquipmentSection
                        projectId={projectId}
                        customerId={project.customerId}
                        jobs={project.jobs}
                        canConfirmOut={canPublish(currentUser, workYardId)}
                    />
                </fieldset>

                <fieldset
                    disabled={!canEdit}
                    style={{border: 0, padding: 0, margin: 0, minWidth: 0}}
                >
                    <ProjectSchedulesBand
                        projectId={projectId}
                        schedules={project.schedules}
                        jobs={project.jobs}
                    />
                </fieldset>

                <ProjectJobsSection
                    projectId={projectId}
                    jobs={project.jobs}
                    projectStatus={project.status}
                    billingTerms={billingTerms}
                />
            </Stack>
        </EditRightsProvider>
    )
}
